import React, { useState, useEffect } from 'react';
import { Input, Select, Table, Button, Checkbox, InputNumber, Tabs, message } from 'antd';
import { SearchOutlined, CloseOutlined, CheckOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline';
import Offcanvas from 'react-bootstrap/Offcanvas';
import '../styles/index.scss'
// import 'bootstrap/dist/css/bootstrap.min.css';
import InputNumberComponentForPrePlan from '../../../../componentsV2/DataEntryComponents/Cluster/InputNumberComponentForPrePlan';
import { submitCollection } from '../../../../_services/submit.service';
import { submitSets } from '../../../UiComponents/SubmitSets';
import { alertService } from '../../../../_services/alert.service';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const { TabPane } = Tabs;

const StoreAdvancedFilterModal = ({ show, onClose, clusterList, onApplyFilter, isRTL, arrayList, preSelectedCluster, preSelectedArray, activeTopTab, showModal, departmentId, onRefreshClusters, dmode, onRefreshArrays, selecedPlusInArray, onShowToast }) => {
    const { t } = useTranslation();
    const [storeCode, setStoreCode] = useState('');
    const [storeName, setStoreName] = useState('');
    const [region, setRegion] = useState('');
    const [tags, setTags] = useState('');
    const [linkedArray, setLinkedArray] = useState('');
    const [width, setWidth] = useState('');
    const [fields, setFields] = useState('');
    const [selectedStoreKeys, setSelectedStoreKeys] = useState([]);
    const [storesData, setStoresData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('1');
    const [active, setActive] = useState("cluster");
    const [sortColumn, setSortColumn] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');
    const [isLinking, setIsLinking] = useState(false);
    const [selectedCluster, setSelectedCluster] = useState(preSelectedCluster?.clusterId || '');
    const [selectedArray, setSelectedArray] = useState(preSelectedArray?.id || '');
    const [modalToast, setModalToast] = useState(null);

    // Modal-specific toast function
    const showModalToast = (type, message, duration = 3000) => {
        setModalToast({ type, message });
        setTimeout(() => setModalToast(null), duration);
    };

    // Get already added store IDs for validation based on active tab
    const getAlreadyAddedStoreIds = () => {
        const storeIds = new Set();

        // Only check stores from the currently active tab (cluster or array)
        if (active === "cluster" && preSelectedCluster && preSelectedCluster.stores) {
            preSelectedCluster.stores.forEach(store => {
                storeIds.add(store.storeId);
            });
        }

        if (active === "array" && preSelectedArray && preSelectedArray.stores) {
            preSelectedArray.stores.forEach(store => {
                storeIds.add(store.storeId);
            });
        }

        return storeIds;
    }; 


    // Unified API function to link stores
    const linkStores = async (clusterId, fieldArrayId, storeIds, departmentId) => {
        try {
            // Prepare payload based on selections
            const payload = {
                storeIds: storeIds,
                departmentID: departmentId,
                clusterId: clusterId ? parseInt(clusterId) : null,
                fieldArrayID: fieldArrayId ? parseInt(fieldArrayId) : null
            };

            // Use /service/cluster/linkToStores endpoint for clusters tab
            const response = await submitSets(
                submitCollection.linkStoresToCluster,
                payload
            );

            if (response?.status) {
                onClose();
                if (onRefreshClusters) {
                    await onRefreshClusters();
                } else if (onRefreshArrays) {
                    await onRefreshArrays();
                }

                const isClearing = clusterId === 'None' || fieldArrayId === 'None';
                if (onShowToast) {
                    if (isClearing) {
                        onShowToast('success', t('NO_STORES_SELECTED'));
                    } else if(active === 'cluster') {
                        onShowToast('success', t('STORE_CONNECTED_CLUSTERS'));
                    } else {
                        onShowToast('success', t('STORE_CONNECTED_ARRAY'));
                    }
                }else {
                    if (isClearing) {
                        showModalToast('success', t('NO_STORES_SELECTED'));
                    } else if(active === 'cluster') {
                        showModalToast('success', t('STORE_CONNECTED_CLUSTERS'));
                    } else {
                        showModalToast('success', t('STORE_CONNECTED_ARRAY'));
                    }
                }
            } else {
                if (onShowToast) {
                    onShowToast('error', response?.extra);
                } else {
                    showModalToast('error', response?.extra);
                }
                showModal(false)
            }
        } catch (error) {
            if (onShowToast) {
                onShowToast('error', t('FAILED_TO_LINK_STORES'));
            } else {
                showModalToast('error', t('FAILED_TO_LINK_STORES'));
            }
        }
    };

    useEffect(() => {
        if (activeTopTab === "clusters" && preSelectedCluster) {
            setActive("cluster"); // Start with cluster tab
            setSelectedCluster(preSelectedCluster.clusterId);
        } else if (activeTopTab === "arrays" && preSelectedArray) {
            setActive("array"); // Start with array tab
            setSelectedArray(preSelectedArray.arrayId || preSelectedArray.key);
        } else {
            setSelectedCluster('');
        }
    }, [preSelectedCluster, preSelectedArray, activeTopTab]);

    useEffect(() => {
        if (show) {
            setSelectedStoreKeys([]);
            fetchFilteredStores();
        } else {
            setStoreCode('');
            setStoreName('');
            setRegion('');
            setTags('');
            setLinkedArray('');
            setWidth('');
            setFields('');
        }
    }, [show]);

    // Fetch stores when filter values change
    useEffect(() => {
        if (show) {
            const timeoutId = setTimeout(() => {
                fetchFilteredStores();
            }, 500); 
            return () => clearTimeout(timeoutId);
        }
    }, [storeCode, storeName, region, tags, linkedArray, width, fields]);

    useEffect(() => {
        setSelectedCluster('');
        setSelectedArray('');
    }, [active]);

    const fetchFilteredStores = async () => {
        setLoading(true);
        try {
            const payload = {
                storeName: storeName || null,
                storeCode: storeCode || null,
                regionName: region || null,
                tagName: tags || null,
                linkedArrayId: linkedArray || null,
                width: width ? parseInt(width) : null,
                noOfFields: fields ? parseInt(fields) : null,
                departmentId: departmentId
            };

            const response = await submitSets(
                submitCollection.filterStores,
                payload
            );

            if (response?.status && response?.extra) {
                const formattedStores = response.extra.map((store, index) => ({
                    key: `store-${store.storeId}-${index}`,
                    id: store.storeId,
                    storeCode: store.storeCode,
                    storeName: store.storeName,
                    regionName: store.regionName,
                    tags: store.tagNames || [],
                    linkedArray: store.linkedArrayName || '',
                    w: `${t('FIELD_ARRAY_LETTERS.W')} ${store.width}`,
                    f: `${t('FIELD_ARRAY_LETTERS.F')} ${store.noOfFields}`
                }));
                setStoresData(formattedStores);
            } else {
                setStoresData([]);
                if (onShowToast) {
                    onShowToast('error', response?.extra || t('FAILED_TO_FETCH_STORES'));
                } else {
                    showModalToast('error', response?.extra || t('FAILED_TO_FETCH_STORES'));
                }
            }
        } catch (error) {
            if (onShowToast) {
                onShowToast('error', t('FAILED_TO_FETCH_STORES'));
            } else {
                showModalToast('error', t('FAILED_TO_FETCH_STORES'));
            }
            setStoresData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (column) => {
        if (sortColumn === column) {
            if (sortDirection === 'asc') {
                setSortDirection('desc');
            } else if (sortDirection === 'desc') {
                setSortColumn(null);
                setSortDirection('asc');
            }
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

    const  getSortedData = (data) => {
        if (!sortColumn) return data;

        return [...data].sort((a, b) => {
            let aVal, bVal;
            
            switch (sortColumn) {
                case 'storeCode':
                case 'storeName':
                case 'regionName':
                case 'linkedArray':
                    aVal = a[sortColumn];
                    bVal = b[sortColumn];
                    break;
                case 'tags':
                    aVal = a.tags.join(',');
                    bVal = b.tags.join(',');
                    break;
                case 'w':
                    aVal = parseInt(a.w.replace(/[^0-9]/g, '')) || 0;
                    bVal = parseInt(b.w.replace(/[^0-9]/g, '')) || 0;
                    break;
                case 'f':
                    aVal = parseInt(a.f.replace(/[^0-9]/g, '')) || 0;
                    bVal = parseInt(b.f.replace(/[^0-9]/g, '')) || 0;
                    break;
                default:
                    return 0;
            }

            if (typeof aVal === 'string') {
                const result = aVal.localeCompare(bVal);
                return sortDirection === 'asc' ? result : -result;
            } else {
                const result = aVal - bVal;
                return sortDirection === 'asc' ? result : -result;
            }
        });
    };

    const SortIcon = ({ column }) => {
        const isActive = sortColumn === column;
        const upColor = isActive && sortDirection === 'asc' ? '#5128A0' : '#828282';
        const downColor = isActive && sortDirection === 'desc' ? '#5128A0' : '#828282';
        
        return (
            <div 
                style={{ 
                    position: 'relative',
                    width: '16px',
                    height: '16px',
                    cursor: 'pointer'
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    handleSort(column);
                }}
            >
                <ArrowUpIcon 
                    style={{ 
                        width: '10px', 
                        height: '10px', 
                        position: 'absolute', 
                        top: '0', 
                        left: '0', 
                        color: upColor,
                        strokeWidth: '2',
                        opacity: isActive && sortDirection === 'asc' ? 1 : 1 
                    }} 
                />
                <ArrowDownIcon 
                    style={{ 
                        width: '10px', 
                        height: '10px', 
                        position: 'absolute', 
                        top: '6px', 
                        left: '6px', 
                        color: downColor,
                        strokeWidth: '2',
                        opacity: isActive && sortDirection === 'desc' ? 1 : 1 
                    }} 
                />
            </div>
        );
    };

    const columns = [
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12px', color: '#666' }}>
                    {t('CODE')}
                    <SortIcon column="storeCode" />
                </div>
            ),
            dataIndex: 'storeCode',
            key: 'storeCode',
            width: 120,
            render: (text, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <Checkbox
                        checked={selectedStoreKeys.includes(record.key)}
                        disabled={getAlreadyAddedStoreIds().has(record.id)}
                        onChange={(e) => {
                            if (e.target.checked) {
                                // Check if store is already added to the current cluster/array
                                const alreadyAddedStoreIds = getAlreadyAddedStoreIds();
                                if (alreadyAddedStoreIds.has(record.id)) {
                                    showModalToast('error', t('STORE_ALREADY_ADDED_TO_SELECTION'));
                                    return;
                                }
                                setSelectedStoreKeys([...selectedStoreKeys, record.key]);
                            } else {
                                setSelectedStoreKeys(selectedStoreKeys.filter(k => k !== record.key));
                            }
                        }}
                    />
                    <span style={{
                        fontSize: '12px',
                        color: getAlreadyAddedStoreIds().has(record.id)
                            ? (dmode ? '#666' : '#999')
                            : (dmode ? '#ccc' : '#333333'),
                        // textDecoration: getAlreadyAddedStoreIds().has(record.id) ? 'line-through' : 'none'
                    }}>
                        {text}
                        {getAlreadyAddedStoreIds().has(record.id) && (
                            <span style={{
                                marginLeft: '8px',
                                fontSize: '10px',
                                color: '#ff7875',
                                fontWeight: '600'
                            }}>
                                ({t('ALREADY_ADDED')})
                            </span>
                        )}
                    </span>
                </div>
            )
        },
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12px', color: '#666' }}>
                    {t('NAME')}
                    <SortIcon column="storeName" />
                </div>
            ),
            dataIndex: 'storeName',
            key: 'storeName',
            width: 120,
            render: (text, record) => (
                <span style={{
                    fontSize: '13px',
                    fontWeight: 500,
                    color: getAlreadyAddedStoreIds().has(record.id)
                        ? (dmode ? '#666' : '#999')
                        : (dmode ? '#ccc' : '#333333'),
                    // textDecoration: getAlreadyAddedStoreIds().has(record.id) ? 'line-through' : 'none'
                }}>
                    {text}
                </span>
            )
        },
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12px', color: '#666' }}>
                    {t('REGION')}
                    <SortIcon column="regionName" />
                </div>
            ),
            dataIndex: 'regionName',
            key: 'regionName',
            width: 100,
              render: (text, record) => (
                <span style={{
                    fontSize: '13px',
                    fontWeight: 500,
                    color: getAlreadyAddedStoreIds().has(record.id)
                        ? (dmode ? '#666' : '#999')
                        : (dmode ? '#ccc' : '#333333'),
                }}>
                    {text}
                </span>
            )
        },
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12px', color: '#666' }}>
                    {t('TAGS')}
                    <SortIcon column="tags" />
                </div>
            ),
            dataIndex: 'tags',
            key: 'tags',
            width: 120,
            render: (tags, record) => {
            const isDisabled = getAlreadyAddedStoreIds().has(record.id);
            
            return (
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {tags.map((tag, idx) => (
                        <span key={idx} style={{
                            backgroundColor: isDisabled 
                                ? (dmode ? '#3a3a3a' : '#e0e0e0')
                                : (dmode ? '#4a5158' : '#F2F1FF'),
                            padding: '2px 6px',
                            borderRadius: '3px',
                            fontSize: '11px',
                            color: isDisabled
                                ? (dmode ? '#666' : '#999')
                                : (dmode ? '#fff' : '#4F4F4F'),
                            fontWeight: 600,
                            opacity: isDisabled ? 0.6 : 1
                        }}>
                            {tag}
                        </span>
                    ))}
                </div>
            );
        }
        },
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12px', color: '#666' }}>
                    {t('LINKED_ARRAY')}
                    <SortIcon column="linkedArray" />
                </div>
            ),
            dataIndex: 'linkedArray',
            key: 'linkedArray',
            width: 120,
            render: (text, record) => <span 
                style={{ fontSize: '12px',   color: getAlreadyAddedStoreIds().has(record.id)
                    ? (dmode ? '#666' : '#999')
                    : (dmode ? '#ccc' : '#333333'), }}>{text}</span>
        },
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginLeft: '5px', fontSize: '12px', color: '#666' }}>
                    {t('FIELD_ARRAY_LETTERS.W')}
                    <SortIcon column="w" />
                </div>
            ),
            dataIndex: 'w',
            key: 'w',
            width: 70,
            align: 'left',
            render: (text, record) => {
            const isDisabled = getAlreadyAddedStoreIds().has(record.id);       
            return (
                <span style={{
                    backgroundColor: isDisabled 
                        ? (dmode ? '#3a3a3a' : '#e0e0e0')
                        : (dmode ? '#4a5158' : '#F2F1FF'),
                    padding: '2px 6px',
                    borderRadius: '3px',
                    fontSize: '11px',
                    color: isDisabled
                        ? (dmode ? '#666' : '#999')
                        : (dmode ? '#fff' : '#4F4F4F'),
                    fontWeight: 600,
                    opacity: isDisabled ? 0.6 : 1
                }}>
                    {text}
                </span>
            );
        }
        },
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12px', color: '#666' }}>
                    {t('FIELD_ARRAY_LETTERS.F')}
                    <SortIcon column="f" />
                </div>
            ),
            dataIndex: 'f',
            key: 'f',
            width: 50,
            align: 'left',
            render: (text, record) => {
            const isDisabled = getAlreadyAddedStoreIds().has(record.id);      
            return (
                <span style={{
                    backgroundColor: isDisabled 
                        ? (dmode ? '#3a3a3a' : '#e0e0e0')
                        : (dmode ? '#4a5158' : '#F2F1FF'),
                    padding: '2px 6px',
                    borderRadius: '3px',
                    fontSize: '11px',
                    color: isDisabled
                        ? (dmode ? '#666' : '#999')
                        : (dmode ? '#fff' : '#4F4F4F'),
                    fontWeight: 600,
                    opacity: isDisabled ? 0.6 : 1
                }}>
                    {text}
                </span>
            );
        }       
        }
    ];

    const handleApplyFilter = () => {
        onApplyFilter?.({
            storeCode, storeName, region, tags, linkedArray, width, fields,
            selectedStores: selectedStoreKeys
        });
    };

    const handleSelectAll = (checked) => {
        if (checked) {
            const alreadyAddedStoreIds = getAlreadyAddedStoreIds();
            // Filter out stores that are already added to the current cluster/array
            const availableStores = storesData.filter(store => !alreadyAddedStoreIds.has(store.id));
            setSelectedStoreKeys(availableStores.map(item => item.key));

            // Show message if some stores were excluded
            const excludedCount = storesData.length - availableStores.length;
            if (excludedCount > 0) {
                if (onShowToast) {
                    onShowToast('warning', t('SOME_STORES_ALREADY_ADDED', { count: excludedCount }));
                } else {
                    showModalToast('warning', t('SOME_STORES_ALREADY_ADDED', { count: excludedCount }));
                }
            }
        } else {
            setSelectedStoreKeys([]);
        }
    };

    const handleLinkToCluster = () => {
        setActive("cluster");
    };

    const handleLinkToArray = () => {
        setActive("array");
    };

    // New function to handle the link operation
    const handleLinkStores = async() => {
        if (!selectedCluster && !selectedArray) {
            if (onShowToast) {
                onShowToast('error', t('PLEASE_SELECT_CLUSTER_OR_ARRAY'));
            } else {
                showModalToast('error', t('PLEASE_SELECT_CLUSTER_OR_ARRAY'));
            }
            return;
        }

        if (selectedStoreKeys.length === 0) {
            if (onShowToast) {
                onShowToast('error', t('PLEASE_SELECT_ONE_STORE'));
            } else {
                showModalToast('error', t('PLEASE_SELECT_ONE_STORE'));
            }
            return;
        }

        if (!departmentId) {
            if (onShowToast) {
                onShowToast('error', t('DEPARTMENT_ID_REQUIRED'));
            } else {
                showModalToast('error', t('DEPARTMENT_ID_REQUIRED'));
            }
            return;
        }

        setIsLinking(true);

        // Get the store IDs from the selected store keys
        const selectedStoreIds = storesData
            .filter(store => selectedStoreKeys.includes(store.key))
            .map(store => store.id);

        await linkStores(selectedCluster, selectedArray, selectedStoreIds, departmentId);

        setSelectedStoreKeys([]);
        setSelectedCluster('');
        setSelectedArray('');
        if (onApplyFilter) {
            onApplyFilter({
                linkedStores: selectedStoreIds,
                clusterId: selectedCluster,
                arrayId: selectedArray
            });
        }

        await onRefreshClusters?.();
        await onRefreshArrays?.();
        setIsLinking(false);
    };

    const hasSelectedItems = selectedStoreKeys.length > 0;

    return (
        <Offcanvas 
            show={show} 
            onHide={onClose} 
            placement={isRTL ? 'start' : 'end'}
            dir={isRTL ? 'rtl' : 'ltr'}
            style={{ 
                width: '960px', 
                maxWidth: '90vw',
                backgroundColor: dmode ? '#2f353e' : '#fff',
                direction: isRTL ? 'rtl' : 'ltr'
            }}
        >
            {/* Header */}
            <Offcanvas.Header 
                closeButton 
                style={{
                    // borderBottom: '1px solid #E8E8E8',
                    padding: '12px 24px 0 24px',
                    // paddingTop: '20px',
                    alignItems: 'flex-start'
                }}
            >
                <div style={{ padding: '16px 0 12px 0' }}>
                    <h2 style={{ 
                        margin: 0, 
                        fontSize: '30px', 
                        fontWeight: '700', 
                        color: dmode ? '#ccc' : '#333333' 
                    }}>
                        {t('STORES_LIST')}
                    </h2>
                    <p style={{ 
                        margin: '4px 0 0', 
                        fontSize: '13px', 
                        color: dmode ? '#ccc' : '#333333',
                        fontWeight: 'normal'
                    }}>
                        {t('SEARCH_UNIT_ADD_TO_ARRAY')}
                    </p>
                </div>
            </Offcanvas.Header>

            {/* Body */}
            <Offcanvas.Body 
                style={{ 
                    padding: 0, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    height: 'calc(100vh - 140px)'
                }}
            >
                {/* Filter Section */}
                <div style={{ 
                    padding: '8px 24px 20px 24px', 
                }}>
                    {/* First Row */}
                    <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: '1fr 1fr 1fr 1fr', 
                        gap: '16px',
                        marginBottom: '16px'
                    }}>
                        <div>
                            <label style={{ 
                                display: 'block', 
                                marginBottom: '8px', 
                                fontSize: '13px', 
                                fontWeight: '600',
                                color: dmode ? '#ccc' : '#333'
                            }}>
                                {t('STORE_CODE')}
                            </label>
                            <Input
                                placeholder={t('TEXT')}
                                value={storeCode}
                                onChange={(e) => setStoreCode(e.target.value)}
                                style={{ 
                                    fontSize: '13px', 
                                    borderRadius: '6px',
                                    height: '32px',
                                    border: '1px solid #828282',
                                    color: dmode ? '#ccc' : '#333333',
                                    backgroundColor: dmode ? '#2f353e' : 'white'
                                }}
                            />
                        </div>
                        <div>
                            <label style={{ 
                                display: 'block', 
                                marginBottom: '8px', 
                                fontSize: '13px', 
                                fontWeight: '600',
                                color: dmode ? '#ccc' : '#333'
                            }}>
                                {t('STORE_NAME')}
                            </label>
                            <Input
                                placeholder={t('TEXT')}
                                value={storeName}
                                onChange={(e) => setStoreName(e.target.value)}
                                style={{ 
                                    fontSize: '13px', 
                                    borderRadius: '6px',
                                    height: '32px',
                                    border: '1px solid #828282',
                                    color: dmode ? '#ccc' : '#333333',
                                    backgroundColor: dmode ? '#2f353e' : 'white'
                                }}
                            />
                        </div>
                        <div>
                            <label style={{ 
                                display: 'block', 
                                marginBottom: '8px', 
                                fontSize: '13px', 
                                fontWeight: '600',
                                color: dmode ? '#ccc' : '#333'
                            }}>
                                {t('REGION')}
                            </label>
                            <Input
                                placeholder={t('TEXT')}
                                value={region}
                                onChange={(e) => setRegion(e.target.value)}
                                style={{ 
                                    fontSize: '13px', 
                                    borderRadius: '6px',
                                    height: '32px',
                                    border: '1px solid #828282',
                                    color: dmode ? '#ccc' : '#333333',
                                    backgroundColor: dmode ? '#2f353e' : 'white'
                                }}
                            />
                        </div>
                        <div>
                            <label style={{ 
                                display: 'block', 
                                marginBottom: '8px', 
                                fontSize: '13px', 
                                fontWeight: '600',
                                color: dmode ? '#ccc' : '#333'
                            }}>
                                {t('TAGS')}
                            </label>
                            <Input
                                placeholder={t('TAG_TAG')}
                                value={tags}
                                onChange={(e) => setTags(e.target.value)}
                                style={{ 
                                    fontSize: '13px', 
                                    borderRadius: '6px',
                                    height: '32px',
                                    border: '1px solid #828282',
                                    color: dmode ? '#ccc' : '#333333',
                                    backgroundColor: dmode ? '#2f353e' : 'white'
                                }}
                            />
                        </div>
                    </div>
                    
                    {/* Second Row */}
                    <div style={{ 
                        display: 'inline-flex', 
                        justifyContent: isRTL ? 'flex-end' : 'flex-start',
                        width: '50%',
                        gap: '16px'
                    }}>
                        <div>
                            <label style={{ 
                                display: 'block', 
                                marginBottom: '8px', 
                                fontSize: '13px', 
                                fontWeight: '600',
                                color: dmode ? '#ccc' : '#333'
                            }}>
                                {t('LINKED_ARRAY')}
                            </label>
                            <div style={{ position: 'relative', display: 'inline-block' }}>
                                <Select
                                    placeholder={t('TEXT')}
                                    value={linkedArray || undefined}
                                    onChange={setLinkedArray}
                                    style={{ 
                                        width: 215, 
                                        borderRadius: '6px',
                                        backgroundColor: dmode ? '#2f353e' : 'white',
                                        color: dmode ? '#ccc' : '#333'
                                    }}
                                    size="middle"
                                    options={[
                                        { value: 'None', label: t('NONE') },
                                        ...arrayList
                                    ]}
                                    suffixIcon={
                                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ marginLeft: '8px' }}>
                                            <path d="M2 4L6 8L10 4" stroke="#999" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    }
                                />
                                <div style={{
                                    position: 'absolute',
                                    right: '32px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    pointerEvents: 'none',
                                    zIndex: 1
                                }}>
                                    {linkedArray && (
                                        <span 
                                            onClick={() => setLinkedArray(null)}
                                            style={{
                                                cursor: 'pointer',
                                                pointerEvents: 'auto',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: '12px',
                                                height: '12px',
                                                backgroundColor: '#bdbdbd',
                                                borderRadius: '50%',
                                                transition: 'opacity 0.2s',
                                                opacity: 0.7
                                            }}
                                            onMouseEnter={(e) => e.target.style.opacity = '1'}
                                            onMouseLeave={(e) => e.target.style.opacity = '0.7'}
                                        >
                                            <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                                                <path d="M6 2L2 6M2 2L6 6" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div style={{display: 'inline-flex', width: '40%'}}>
                            <InputNumberComponentForPrePlan
                                label={t('WIDTH')}
                                labelStyle={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                }}
                                style={{
                                    color: '#333333'
                                }}
                                min={0}
                                max={1000000000}
                                onValueChange={(val) => setWidth(val ?? '')}
                                name="width"
                                placeholder={t('NUM')}
                                dir="ltr"
                                dmode={dmode}
                                inputGroupWidth="90%"
                                value={width}
                                handleKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        handleApplyFilter();
                                    }
                                }}
                            />
                            <InputNumberComponentForPrePlan
                                label={t('FIELDS')}
                                labelStyle={{
                                    display: 'block',
                                    marginBottom: '8px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                }}
                                style={{
                                    color: '#333333'
                                }}
                                min={0}
                                max={1000000000}
                                onValueChange={(val) => setFields(val ?? '')}
                                name="fields"
                                placeholder={t('NUM')}
                                dir="ltr"
                                dmode={dmode}
                                inputGroupWidth="90%"
                                value={fields}
                                handleKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        handleApplyFilter();
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>

                {/* Table Section */}
                <div style={{ 
                    flex: 1, 
                    overflow: 'auto', 
                    padding: '0 14px 10px 14px',
                    paddingTop: '16px'
                }}>
                    <style>
                        {`.ant-table-tbody > tr {
                            cursor: pointer !important;
                        }
                        .ant-table-tbody > tr:hover {
                            background-color: #f5f5f5 !important;
                        }
                        .ant-table-tbody > tr .ant-select {
                            cursor: default !important;
                        }
                        .ant-table-tbody > tr .ant-checkbox-wrapper {
                            cursor: default !important;
                        }`}
                    </style>
                    <Table
                        columns={columns}
                        dataSource={getSortedData(storesData)}
                        pagination={false}
                        size="small"
                        loading={loading}
                        rowClassName={() => ''}
                        locale={{
                            emptyText: 
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: 'calc(100vh - 450px)',
                                    width: '100%',
                                    color: '#666666',
                                    fontSize: '14px',
                                    fontFamily: 'Mulish, sans-serif',
                                    fontWeight: '500',
                                    overflow: 'hidden'
                                }}>
                                    <div style={{ textAlign: 'center', direction: isRTL === 'rtl' ? 'rtl' : 'ltr', width: '100%' }}>
                                        {storesData.length === 0 && (storeCode || storeName || region || tags || linkedArray || width || fields) ? t('NO_STORES_MATCH_CRITERIA') : t('NO_STORES_AVAILABLE')}
                                    </div>
                                </div>  
                        }}
                        onRow={(record) => {
                            return {
                                onClick: (event) => {
                                    // Prevent selection if clicking on Select component or checkbox
                                    if (event.target.closest('.ant-select') || event.target.closest('.ant-checkbox-wrapper')) {
                                        return;
                                    }

                                    // Prevent selection if store is already added
                                    const alreadyAddedStoreIds = getAlreadyAddedStoreIds();
                                    if (alreadyAddedStoreIds.has(record.id)) {
                                        showModalToast('error', t('STORE_ALREADY_ADDED_TO_SELECTION'));
                                        return;
                                    }

                                    const key = record.key;
                                    const isSelected = selectedStoreKeys.includes(key);

                                    if (isSelected) {
                                        setSelectedStoreKeys(selectedStoreKeys.filter(k => k !== key));
                                    } else {
                                        setSelectedStoreKeys([...selectedStoreKeys, key]);
                                    }
                                }
                            };
                        }}
                    />
                </div>

                {/* Footer with Tabs - Only show when items are selected */}
                {hasSelectedItems && (
                    <div style={{
                        position: 'relative',
                        padding: '0 0 16px 0',
                        backgroundColor: dmode ? '#2f353e' : '#fff',
                        marginTop: 'auto'
                    }}
                    className="footer-with-border"
                    >
                        {/* Name Tags Section */}
                        <div style={{ 
                            padding: '18px 16px 0 16px',
                        }}>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                                {selectedStoreKeys.map((storeKey, index) => {
                                    const store = storesData.find(s => s.key === storeKey);
                                    return store ? (
                                        <span 
                                            key={storeKey}
                                            style={{
                                                backgroundColor: '#F2F1FF',
                                                color: '#5128A0',
                                                padding: '2px 6px',
                                                borderRadius: '6px',
                                                fontSize: '13px',
                                                fontWeight: '700',
                                                cursor: 'default',
                                                transition: 'all 0.2s ease',
                                                maxWidth: '150px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {store.storeName}
                                        </span>
                                    ) : null;
                                })}
                                {selectedStoreKeys.length === 0 && (
                                    <span style={{ color: '#666', fontSize: '13px', fontStyle: 'italic' }}>
                                        {t('NO_STORES_SELECTED')}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Footer Actions */}
                        <div style={{ 
                            padding: '0 16px 16px 16px',
                            display: 'flex', 
                            justifyContent: 'space-between',
                            alignItems: 'flex-end',
                        }}>
                            {/* Left side - Action buttons */}
                            <div style={{ display: 'flex', gap: '0', alignItems: 'center' }}>
                                <button
                                    onClick={handleLinkToCluster}
                                    style={{
                                        backgroundColor: active === "cluster" ? "#4A4A4A" : "transparent",
                                        color: active === "cluster" ? "white" : "#999",
                                        border: '1px solid #D9D9D9',
                                        padding: '8px 16px',
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        borderRadius: '6px 0 0 6px',
                                        cursor: 'pointer',
                                        height: '36px',
                                        minWidth: '120px'
                                    }}
                                >
                                    {t('LINK_TO_CLUSTER')}
                                </button>
                                <button
                                    onClick={handleLinkToArray}
                                    style={{
                                        backgroundColor: active === "array" ? "#4A4A4A" : "transparent",
                                        color: active === "array" ? "white" : "#999",
                                        border: '1px solid #D9D9D9',
                                        padding: '8px 16px',
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        borderRadius: '0 6px 6px 0',
                                        borderLeft: 'none',
                                        cursor: 'pointer',
                                        height: '36px',
                                        minWidth: '120px'
                                    }}
                                >
                                    {t('LINK_TO_ARRAY')}
                                </button>
                            </div>

                            {/* Right side - Cluster selection and Link button */}
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
                                    <div style={{ fontSize: '13px', color: dmode ? '#ccc' : '#333', marginBottom: '4px', fontWeight: 600 }}>
                                    {active === "cluster" ? t('CHOOSE_CLUSTER') : t('CHOOSE_ARRAY')}
                                    </div>
                                    <Select
                                        placeholder={t('CHOOSE')}
                                        value={active === 'cluster' ? (selectedCluster || undefined) : (selectedArray || undefined)}
                                        onChange={active === 'cluster' ? setSelectedCluster : setSelectedArray}
                                        // disabled={(() => {
                                        //     if (activeTopTab === "clusters" && preSelectedCluster && active === "cluster") {
                                        //         return true; 
                                        //     }
                                        //     if (activeTopTab === "arrays" && preSelectedArray && active === "array") {
                                        //         return true; 
                                        //     }
                                        //     return false; 
                                        // })()}
                                        style={{ 
                                            width: '190px',
                                            fontSize: '13px'
                                        }}
                                        size="middle"
                                        options={(() => {
                                            if (activeTopTab === "clusters" && preSelectedCluster) {
                                                if (active === "cluster") {
                                                    return [{ value: preSelectedCluster.clusterId, label: preSelectedCluster.clusterName }];
                                                } else {
                                                    return [{ value: 'None', label: t('NONE') }, ...arrayList];
                                                }
                                            }
                                            
                                            if (activeTopTab === "arrays" && preSelectedArray) {
                                                
                                                if (active === "cluster") {
                                                    return [{ value: 'None', label: t('NONE') }, ...clusterList];
                                                } else {
                                                    return [{ value: preSelectedArray.arrayId || preSelectedArray.key, label: preSelectedArray.arrayName }];
                                                }
                                            }

                                            if(activeTopTab === "arrays" && !preSelectedArray && selecedPlusInArray) {
                                                
                                                if (active === "cluster") {
                                                    return [{ value: 'None', label: t('NONE') }, ...clusterList];
                                                } else {
                                                    return [{ value: preSelectedArray.arrayId || preSelectedArray.key, label: preSelectedArray.arrayName }];
                                                }
                                            }
                                            
                                            // Show all options for both tabs
                                            if (active === "cluster") {
                                                return [{ value: 'None', label: t('NONE') }, ...clusterList];
                                            } else {
                                                return [{ value: 'None', label: t('NONE') }, ...arrayList];
                                            }
                                        })()}
                                    />
                                </div>
                                <Button
                                    type="primary"
                                    onClick={handleLinkStores}
                                    loading={isLinking}
                                    disabled={selectedStoreKeys.length === 0}
                                    style={{ 
                                        borderRadius: '4px',
                                        backgroundColor: '#219653',
                                        borderColor: '#219653',
                                        fontSize: '13px',
                                        height: '32px'
                                    }}
                                >
                                    {t('LINK')} {<CheckCircleOutlined style={{ fontSize: '16px' }} />}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Original Footer - Show when no items selected */}
                {!hasSelectedItems && (
                    <div style={{ 
                        position: 'relative',
                        padding: '40px 24px',
                        display: 'flex', 
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: dmode ? '#2f353e' : '#fff',
                        marginTop: 'auto',
                        minHeight: '129px'
                    }}
                    className="footer-with-border"
                    >
                        <div style={{ 
                            fontSize: '14px', 
                            color: dmode ? '#666' : '#BDBDBD',
                            fontWeight: 700,
                            textAlign: 'center'
                        }}>
                            {t('SELECT_STORES_TO_LINK')}
                        </div>
                    </div>
                )}
            </Offcanvas.Body>

       {modalToast && (
                <div style={{
                    position: 'fixed',
                    top: '13%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 10001,
                    padding: '12px 16px',
                    borderRadius: '6px',
                    backgroundColor: modalToast.type === 'success' ? '#4CAF50' : '#EB5757',
                    color: 'white',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    fontSize: '14px',
                    fontWeight: '600',
                }}>
                    <div style={{                    
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}>
                        <span>{modalToast.message}</span>
                        <span 
                            onClick={() => setModalToast(null)}
                            style={{
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                padding: '2px',
                                transition: 'opacity 0.2s',
                                opacity: 1, 
                                marginLeft: '8px',
                            }}
                            onMouseEnter={(e) => e.target.style.opacity = '0.7'}
                            onMouseLeave={(e) => e.target.style.opacity = '1'}
                        >
                            <X size={16} />
                        </span>
                    </div>
                </div>
            )}

          <style jsx>{`
            
            .offcanvas .ant-input {
                color: ${dmode ? '#ccc' : '#333333'} !important;
            }
            .offcanvas .ant-input::placeholder {
                color: ${dmode ? '#777' : '#999'} !important;
            }
            .offcanvas .ant-input:focus {
                color: ${dmode ? '#fff' : '#333333'} !important;
                background-color: ${dmode ? '#3a4149' : '#fff'} !important;
            }
            .offcanvas .custom-input-number-pp input,
            .offcanvas .rc-input-number-input {
                color: ${dmode ? '#ccc' : '#333333'} !important;
            }
            .offcanvas .custom-input-number-pp input::placeholder,
            .offcanvas .rc-input-number-input::placeholder {
                color: #999 !important;
            }
            .offcanvas .ant-table-tbody > tr:hover > td {
                background-color: ${dmode ? '#3a4149' : '#FFF6E5'} !important;
            }
            .offcanvas .ant-table-tbody > tr:hover > td span[style*="background"] {
                background-color: ${dmode ? '#5a6168' : '#FFEAC3'} !important;
                color: ${dmode ? '#fff' : '#333'} !important;
            }
            .ant-select .ant-select-selector::before {
                content: '';
                position: absolute;
                right: 24px;
                top: 0;
                bottom: 0;
                width: 0;
                border-left: 1px solid #828282;
                z-index: 1;
            }
            .ant-select .ant-select-selector {
                border: 1px solid #828282 !important;
            }
            .ant-select:hover .ant-select-selector {
                border: 1px solid #828282 !important;
            }
            .ant-select.ant-select-focused .ant-select-selector {
                border: 1px solid #828282 !important;
                box-shadow: 0 0 0 2px rgba(130, 130, 130, 0.1) !important;
            }
            .ant-select .ant-select-arrow {
                right: 7px !important;
                color: #828282 !important;
            }
            .ant-select .ant-select-arrow .anticon {
                color: #828282 !important;
            }
            ${dmode ? `
            .ant-select .ant-select-selector {
                background-color: #2f353e !important;
                color: #ccc !important;
                border: 1px solid #828282 !important;
            }
            .ant-select:hover .ant-select-selector {
                background-color: #2f353e !important;
                color: #ccc !important;
                border: 1px solid #828282 !important;
            }
            .ant-select.ant-select-focused .ant-select-selector {
                background-color: #2f353e !important;
                color: #ccc !important;
                border: 1px solid #828282 !important;
                box-shadow: 0 0 0 2px rgba(130, 130, 130, 0.1) !important;
            }
            .ant-select .ant-select-selection-item {
                color: #ccc !important;
            }
            .ant-select .ant-select-selection-placeholder {
                color: #777 !important;
            }
            .ant-select-dropdown {
                background-color: #2f353e !important;
            }
            .ant-select-item {
                background-color: #2f353e !important;
                color: #ccc !important;
            }
            .ant-select-item:hover {
                background-color: #3a4149 !important;
                color: #302f2fff !important;
            }
            .ant-select-item-option-selected {
                background-color: #4a5158 !important;
                color: #302f2fff !important;
            }
            ` : ''}
            ${dmode ? `
            .ant-table-wrapper {
                background-color: #2f353e !important;
            }
            .ant-table {
                background-color: #2f353e !important;
                color: #ccc !important;
            }
            .ant-table-container {
                background-color: #2f353e !important;
            }
            .ant-spin-container {
                background-color: #2f353e !important;
            }
            .ant-empty {
                color: #ccc !important;
            }
            .ant-empty-description {
                color: #999 !important;
            }
            ` : ''}
            .offcanvas .ant-table-thead > tr > th {
                background-color: ${dmode ? '#2f353e' : 'transparent'} !important;
                font-size: 12px !important;
                font-weight: 500 !important;
                color: ${dmode ? '#ccc' : '#666'} !important;
                padding: 4px 12px !important; /* Reduced padding for smaller header height */
                border-bottom: 1px solid ${dmode ? '#555' : '#BDBDBD'} !important;
                border-right: 1px solid ${dmode ? '#555' : '#BDBDBD'} !important;
                position: relative !important;
            }
            .offcanvas .ant-table-thead > tr > th:last-child {
                border-right: none !important;
            }
            .offcanvas .ant-table-thead > tr > th::before {
                content: '';
                position: absolute;
                right: 0;
                top: 0;
                bottom: 0;
                width: 1px;
                background-color: ${dmode ? '#555' : '#BDBDBD'};
                display: block;
            }
            .offcanvas .ant-table-thead > tr > th:last-child::before {
                display: none;
            }
            .footer-with-border::before {
                content: '';
                position: absolute;
                top: 0;
                left: 16px;
                right: 16px;
                height: 1.5px;
                background-color: #4F4F4F;
                transform: scaleY(0.5);
                transform-origin: top;
            }
            .offcanvas .ant-table-thead > tr > th:first-child {
                border-top-left-radius: 0 !important;
            }
            .offcanvas .ant-table-thead > tr > th:last-child {
                border-top-right-radius: 0 !important;
            }
            .offcanvas .ant-table-tbody > tr > td {
                padding: 14px 14px !important; /* Increased padding for bigger drawer height */
                font-size: 13px !important;
                border-bottom: 1px solid ${dmode ? '#555' : '#BDBDBD'} !important;
                background-color: ${dmode ? '#2f353e' : 'white'} !important;
                color: ${dmode ? '#ccc' : '#333'} !important;
            }
            .offcanvas .ant-table-tbody > tr:hover > td {
                border-bottom: ${dmode ?'1px solid green' :'1px solid #5128A0'}   !important;
            }
            .offcanvas-header .btn-close {
                margin: 0;
                padding: 0.5rem;
                background-color: #F2F1FF;
                border-radius: 50%;
            }
            .ant-tabs .ant-tabs-tab {
                font-size: 13px !important;
                padding: 8px 0 !important;
                margin: 0 16px !important;
            }
            .ant-tabs .ant-tabs-content-holder {
                display: none !important;
            }
            .ant-tabs-nav {
                margin-bottom: 0 !important;
            }
            .btn-close {
                background: transparent url("data:image/svg+xml,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 16 16%27 fill=%27%23000%27%3e%3cpath d=%27M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414z%27/%3e%3c/svg%3e") center/0.75em auto no-repeat!important;
                background-color: #F2F1FF!important;
            }
            .offcanvas .ant-empty {
                border: none !important;
                background: transparent !important;
            }
            .offcanvas .ant-empty:hover {
                background: transparent !important;
            }
            .offcanvas .ant-table-placeholder {
                border: none !important;
                background: transparent !important;
            }
            .offcanvas .ant-table-placeholder:hover {
                background: transparent !important;
            }
            .offcanvas .ant-table-placeholder .ant-table-cell {
                border-bottom: none !important;
                background: transparent !important;
            }
            .offcanvas .ant-table-placeholder .ant-table-cell:hover {
                background: transparent !important;
                border-bottom: none !important;
            }
            .offcanvas .ant-table-tbody .ant-table-placeholder > td {
                border-bottom: none !important;
                background: transparent !important;
            }
            .offcanvas .ant-table-tbody .ant-table-placeholder > td:hover {
                background: transparent !important;
                border-bottom: none !important;
            }
        `}</style>

        </Offcanvas>
    );
};

export default StoreAdvancedFilterModal;